import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useAction } from '../../context';
import { Failure, Field } from '../../bits';
import { useT } from '../../../messages';
import type { CatalogItem } from '../../../wire';
import { money, secondLocaleOf, useFreshItem } from './text';

type Option = CatalogItem['options'][number];

interface OptionDraft {
  name: string;
  /** The other language of the pair (`secondLocaleOf`); `''` sends nothing new. */
  nameSecond: string;
  price: string;
  minutes: string;
}

/**
 * **The ways one treatment is sold** — `docs/catalog-options-design.md`, the
 * console's half, drawn as the table the mockup shows: one row per length, its
 * price, whether it is the one quoted, whether it is still sold.
 *
 * Four rules are drawn here rather than encoded, because each is a rule of the
 * *catalog* and not of this screen:
 *
 * **No delete, and there is not going to be one.** `appointments.option_id` is
 * `ON DELETE RESTRICT`: a length somebody was sold still has to appear on last
 * March's report. "Still sold" is how one stops being offered, and the default
 * then falls through to the first live one.
 *
 * **Blank minutes is a fact, not an empty field.** `null` says *this option does
 * not change the length*, as against a number, which sets it.
 *
 * **Default and Still sold write immediately; name, price and minutes wait for
 * Save.** A radio that needed saving could show two defaults at once, which is a
 * state the database's partial unique index does not have.
 *
 * **Reordering is a PATCH per row that moved** — there is no wholesale option
 * write, because an option may not be deleted — sent one at a time, because a
 * transaction object is sequential (dev.md §6).
 */
export function ItemOptions({
  tenantId,
  item,
  locale,
  commerceEditable,
  onChanged,
}: {
  tenantId: string;
  item: CatalogItem;
  /** The tenant's default locale — what the plain name box writes under. */
  locale: string;
  commerceEditable: boolean;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [shown, setShown] = useFreshItem(item);
  const [drafts, setDrafts] = useState<Record<string, OptionDraft>>({});
  const [ref, setRef] = useState('');
  const [name, setName] = useState('');
  const [nameSecond, setNameSecond] = useState('');
  const [price, setPrice] = useState('');
  const [minutes, setMinutes] = useState('');

  const options = shown.options;
  /**
   * **The second language** (L8). A name with words in that box is sent as the
   * map the item's name already travels as — `{ en, es }` — and a name without
   * is sent as the plain string it always was. The read answers one resolved
   * string per option, so this box opens blank on every row: it can *give* a
   * length its Spanish, and cannot yet show the Spanish a file gave it.
   */
  const second = secondLocaleOf(locale);
  const language = t(
    second === 'es' ? 'catalog.options.language.es' : 'catalog.options.language.en',
  );
  const localizedName = (primary: string, other: string): string | Record<string, string> =>
    other.trim() === '' ? primary.trim() : { [locale]: primary.trim(), [second]: other.trim() };

  const currentOf = (o: Option): OptionDraft => ({
    name: o.name,
    nameSecond: '',
    price: money(o.price_cents),
    minutes: o.duration_min === null ? '' : String(o.duration_min),
  });
  const valueOf = (o: Option): OptionDraft => drafts[o.id] ?? currentOf(o);

  const patchDraft = (id: string, next: Partial<OptionDraft>, base: OptionDraft): void => {
    action.clear();
    setDrafts({ ...drafts, [id]: { ...base, ...next } });
  };

  const forget = (id: string): void => {
    const next = { ...drafts };
    delete next[id];
    setDrafts(next);
  };

  const saveRow = async (o: Option): Promise<void> => {
    const value = valueOf(o);
    const current = currentOf(o);
    const cents = Math.round(Number(value.price) * 100);
    const ok = await action.run(async () => {
      const answer = await api.updateOption(tenantId, o.id, {
        ...(value.name === current.name && value.nameSecond.trim() === ''
          ? {}
          : { name: localizedName(value.name, value.nameSecond) }),
        ...(value.price === current.price ? {} : { price_cents: cents }),
        ...(value.minutes === current.minutes
          ? {}
          : { duration_min: value.minutes.trim() === '' ? null : Number(value.minutes) }),
      });
      setShown(answer.item);
    });
    if (ok) {
      forget(o.id);
      onChanged();
    }
  };

  const write = async (
    optionId: string,
    body: { is_default?: boolean; active?: boolean },
  ): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.updateOption(tenantId, optionId, body);
      setShown(answer.item);
    });
    if (ok) onChanged();
  };

  const move = async (index: number, by: number): Promise<void> => {
    const to = index + by;
    if (to < 0 || to >= options.length) return;
    const next = [...options];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved as Option);

    const ok = await action.run(async () => {
      for (const [position, option] of next.entries()) {
        if (option.sort_order === position) continue;
        const answer = await api.updateOption(tenantId, option.id, { sort_order: position });
        setShown(answer.item);
      }
    });
    if (ok) onChanged();
  };

  const newCents = Math.round(Number(price) * 100);
  /**
   * A `ref` this item already has: the server's `409` stays the fence, and this
   * is the sentence before the trip. Trimmed, because that is what is sent.
   */
  const refTaken = options.some((o) => o.ref === ref.trim());
  const canAdd =
    commerceEditable &&
    ref.trim() !== '' &&
    !refTaken &&
    name.trim() !== '' &&
    price.trim() !== '' &&
    Number.isFinite(newCents) &&
    newCents >= 0 &&
    (minutes.trim() === '' || Number(minutes) > 0);

  const add = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.createOption(tenantId, item.item_id, {
        ref: ref.trim(),
        name: localizedName(name, nameSecond),
        price_cents: newCents,
        ...(minutes.trim() === '' ? {} : { duration_min: Number(minutes) }),
      });
      setShown(answer.item);
    });
    if (ok) {
      setRef('');
      setName('');
      setNameSecond('');
      setPrice('');
      setMinutes('');
      onChanged();
    }
  };

  // Show only what applies: a live business's service sold one way has no
  // lengths to show and none this console may add.
  if (!commerceEditable && options.length === 0) return null;

  return (
    <div className="catalog-block" data-options={item.item_id}>
      <h4 className="catalog-block__title">{t('catalog.options')}</h4>
      <p className={commerceEditable ? 'muted' : 'catalog-fence-note'}>
        {commerceEditable ? t('catalog.lengths.note') : t('catalog.lengths.fenced')}
      </p>

      {options.length === 0 ? (
        <p className="muted">{t('catalog.lengths.empty')}</p>
      ) : (
        <div className="catalog-scroll">
          <table className="catalog-lengths">
            <thead>
              <tr>
                <th>{t('catalog.options.name')}</th>
                <th>{t('catalog.options.nameIn', { language })}</th>
                <th>{t('catalog.options.price')}</th>
                <th>{t('catalog.options.minutes')}</th>
                <th>{t('catalog.options.default')}</th>
                <th>{t('catalog.options.active')}</th>
                <th>
                  <span className="catalog-sr">{t('catalog.lengths.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {options.map((option, index) => {
                const value = valueOf(option);
                const current = currentOf(option);
                const dirty =
                  value.name !== current.name ||
                  value.nameSecond.trim() !== '' ||
                  value.price !== current.price ||
                  value.minutes !== current.minutes;
                const labels = { item: item.item_id, ref: option.ref };
                return (
                  <tr
                    key={option.id}
                    data-option={option.id}
                    className={option.active ? undefined : 'is-retired'}
                  >
                    <td>
                      <input
                        className="catalog-cell"
                        aria-label={t('catalog.options.nameOf', labels)}
                        value={value.name}
                        disabled={!commerceEditable}
                        onChange={(e) => patchDraft(option.id, { name: e.target.value }, value)}
                      />
                    </td>
                    <td>
                      <input
                        className="catalog-cell"
                        aria-label={t('catalog.options.nameInOf', { ...labels, language })}
                        value={value.nameSecond}
                        disabled={!commerceEditable}
                        onChange={(e) =>
                          patchDraft(option.id, { nameSecond: e.target.value }, value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="catalog-cell catalog-cell--num"
                        inputMode="decimal"
                        aria-label={t('catalog.options.priceOf', labels)}
                        value={value.price}
                        disabled={!commerceEditable}
                        onChange={(e) => patchDraft(option.id, { price: e.target.value }, value)}
                      />
                    </td>
                    <td>
                      <input
                        className="catalog-cell catalog-cell--num"
                        inputMode="numeric"
                        aria-label={t('catalog.options.minutesOf', labels)}
                        placeholder={t('catalog.lengths.sameLength')}
                        value={value.minutes}
                        disabled={!commerceEditable}
                        onChange={(e) => patchDraft(option.id, { minutes: e.target.value }, value)}
                      />
                    </td>
                    <td className="catalog-lengths__mark">
                      <input
                        type="radio"
                        // One group per item: the database admits one default
                        // per item by a partial unique index, and a radio group
                        // is the only control that says so without being told.
                        name={`default-${item.item_id}`}
                        aria-label={t('catalog.options.defaultOf', labels)}
                        checked={option.is_default}
                        disabled={!commerceEditable}
                        onChange={() => void write(option.id, { is_default: true })}
                      />
                    </td>
                    <td className="catalog-lengths__mark">
                      <input
                        type="checkbox"
                        aria-label={t('catalog.options.activeOf', labels)}
                        checked={option.active}
                        disabled={!commerceEditable}
                        onChange={(e) => void write(option.id, { active: e.target.checked })}
                      />
                    </td>
                    <td>
                      <div className="catalog-lengths__actions">
                        <button
                          type="button"
                          className="button button--quiet button--sm"
                          aria-label={t('catalog.options.up', labels)}
                          disabled={!commerceEditable || index === 0 || action.busy}
                          onClick={() => void move(index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="button button--quiet button--sm"
                          aria-label={t('catalog.options.down', labels)}
                          disabled={
                            !commerceEditable || index === options.length - 1 || action.busy
                          }
                          onClick={() => void move(index, 1)}
                        >
                          ↓
                        </button>
                        {dirty ? (
                          <button
                            type="button"
                            className="button button--primary button--sm"
                            aria-label={t('catalog.options.saveOf', labels)}
                            disabled={!commerceEditable || action.busy}
                            onClick={() => void saveRow(option)}
                          >
                            {t('catalog.options.save')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {commerceEditable ? (
        <details className="catalog-add">
          <summary>{t('catalog.options.add')}</summary>
          <p className="muted">{t('catalog.options.addNote')}</p>
          <div className="catalog-fields">
            <Field
              label={t('catalog.options.ref')}
              hint={
                refTaken
                  ? t('catalog.options.refTaken', { ref: ref.trim() })
                  : t('catalog.lengths.refHint')
              }
            >
              <input
                aria-label={t('catalog.options.newRef', { item: item.item_id })}
                aria-invalid={refTaken}
                value={ref}
                onChange={(e) => setRef(e.target.value)}
              />
            </Field>
            <Field label={t('catalog.options.name')}>
              <input
                aria-label={t('catalog.options.newName', { item: item.item_id })}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label={t('catalog.options.nameIn', { language })}>
              <input
                aria-label={t('catalog.options.newNameIn', { item: item.item_id, language })}
                value={nameSecond}
                onChange={(e) => setNameSecond(e.target.value)}
              />
            </Field>
            <Field label={t('catalog.options.price')}>
              <input
                inputMode="decimal"
                aria-label={t('catalog.options.newPrice', { item: item.item_id })}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </Field>
            <Field label={t('catalog.options.minutes')} hint={t('catalog.options.minutesHint')}>
              <input
                inputMode="numeric"
                aria-label={t('catalog.options.newMinutes', { item: item.item_id })}
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
              />
            </Field>
          </div>
          <div className="row-actions">
            <button
              type="button"
              className="button button--sm"
              aria-label={t('catalog.options.createOf', { item: item.item_id })}
              disabled={!canAdd || action.busy}
              onClick={() => void add()}
            >
              {t('catalog.options.create')}
            </button>
          </div>
        </details>
      ) : null}

      {action.failure !== null ? <Failure failure={action.failure} /> : null}
    </div>
  );
}
